import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Phone } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";

const tone = (s) => (s >= 85 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700");

const Row = ({ it, pick, on, onPick, onToggle }) => (
  <div className="flex flex-wrap items-center gap-2 px-3 py-2" data-testid={`ks-row-${it.rider_id}`}>
    <input type="checkbox" checked={on} disabled={!pick} onChange={onToggle} className="h-4 w-4 accent-indigo-600" data-testid={`ks-check-${it.rider_id}`} />
    <div className="min-w-0 w-60">
      <p className="truncate text-sm font-semibold text-slate-800" dir="ltr">{it.name || "—"}</p>
      <p className="text-[11px] text-slate-500"><span className="font-mono" dir="ltr">{it.rider_id}</span> · {it.platform} · {it.orders} طلب</p>
    </div>
    <span className="text-slate-300">←</span>
    {it.alternatives.length ? (
      <select value={pick || ""} onChange={(e) => onPick(e.target.value)} className="h-8 min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-1 text-xs" data-testid={`ks-select-${it.rider_id}`}>
        {it.alternatives.map((a) => <option key={a.employee_id} value={a.employee_id}>{a.name} ({a.score}%{a.phone_match ? " · جوال مطابق" : ""})</option>)}
      </select>
    ) : <span className="flex-1 text-xs text-slate-400">لا يوجد اسم قريب (أقل من 70%)</span>}
    {pick && (() => { const a = it.alternatives.find((x) => x.employee_id === pick); return a && (
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${tone(a.score)}`} data-testid={`ks-score-${it.rider_id}`}>
        {a.phone_match && <Phone className="h-3 w-3" />}{a.score}%
      </span>); })()}
    {it.ambiguous && <span className="text-[10px] text-amber-600">أكثر من اسم متقارب</span>}
  </div>
);

export const KeetaSuggestButton = ({ month, invId, onDone }) => {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [picks, setPicks] = useState({});
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setOpen(true); setD(null);
    try {
      const r = await api.keetaSuggest({ month, inv_id: invId });
      setD(r);
      setPicks(Object.fromEntries(r.items.filter((x) => x.suggested).map((x) => [x.rider_id, x.suggested.employee_id])));
      setSel(new Set(r.items.filter((x) => x.auto_select).map((x) => x.rider_id)));
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر جلب الاقتراحات"); setOpen(false); }
  };
  const toggle = (rid) => setSel((s) => { const n = new Set(s); n.has(rid) ? n.delete(rid) : n.add(rid); return n; });
  const chosen = [...sel].filter((rid) => picks[rid]);
  const platOf = Object.fromEntries((d?.items || []).map((x) => [x.rider_id, x.platform]));
  const dup = chosen.length !== new Set(chosen.map((rid) => `${picks[rid]}|${platOf[rid]}`)).size;
  const save = async () => {
    if (dup) return toast.error("نفس الموظف مختار لأكثر من معرّف في نفس الفاتورة — عدّل الاختيار");
    setBusy(true);
    try {
      const r = await api.keetaLinkBulk(chosen.map((rid) => ({ rider_id: rid, employee_id: picks[rid] })));
      toast.success(`تم ربط ${r.linked} معرّف كيتا`); setOpen(false); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الربط"); } finally { setBusy(false); }
  };
  const items = d?.items || [];
  return (
    <>
      <Button size="sm" variant="outline" className="h-8 gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50" onClick={start} data-testid="keeta-suggest-btn">
        <Sparkles className="h-3.5 w-3.5" /> اقتراح الربط بالاسم
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-3xl font-body max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="keeta-suggest-dialog">
          <DialogHeader className="text-right">
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-indigo-600" /> اقتراح ربط معرّفات كيتا</DialogTitle>
            <DialogDescription>مقارنة اسم السائق (إنجليزي) بأسماء الموظفين (عربي/إنجليزي) والجوال إن وُجد. المحدّد تلقائياً ≥ 85% — راجع قبل الربط.</DialogDescription>
          </DialogHeader>
          {!d ? <div className="py-12 text-center text-slate-300"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div> : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                <span data-testid="ks-stats">غير مربوط: <b>{d.unmatched}</b> · لها اقتراح: <b>{d.with_suggestion}</b></span>
                <button className="text-indigo-600 underline" onClick={() => setSel(new Set(items.filter((x) => picks[x.rider_id]).map((x) => x.rider_id)))} data-testid="ks-select-all">تحديد كل المقترح</button>
                <button className="text-slate-500 underline" onClick={() => setSel(new Set())} data-testid="ks-clear">إلغاء التحديد</button>
                <Button size="sm" className="ms-auto h-8 gap-1.5 bg-indigo-600 hover:bg-indigo-700" disabled={!chosen.length || busy} onClick={save} data-testid="ks-save">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} ربط المحدد ({chosen.length})
                </Button>
              </div>
              {dup && <p className="text-xs text-rose-600" data-testid="ks-dup">تنبيه: نفس الموظف مختار لأكثر من معرّف</p>}
              <div className="divide-y divide-slate-100 rounded-xl border border-slate-200" data-testid="ks-list">
                {items.length === 0 && <p className="p-6 text-center text-sm text-slate-400">كل معرّفات كيتا مربوطة</p>}
                {items.map((it) => <Row key={it.rider_id} it={it} pick={picks[it.rider_id]} on={sel.has(it.rider_id)}
                  onPick={(v) => setPicks((p) => ({ ...p, [it.rider_id]: v }))} onToggle={() => toggle(it.rider_id)} />)}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
